import React, { useState } from 'react';
import { MessageCircleWarning, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const MODULES = [
  { v: 'invoice', label: 'Invoice' },
  { v: 'ledger', label: 'Ledger' },
  { v: 'receipt', label: 'Receipt' },
  { v: 'advance', label: 'Advance' },
  { v: 'reports', label: 'Reports' },
  { v: 'parties', label: 'Parties' },
  { v: 'auth', label: 'Login / Auth' },
  { v: 'dashboard', label: 'Dashboard' },
  { v: 'accounting', label: 'Accounting' },
  { v: 'gst', label: 'GST' },
  { v: 'tds', label: 'TDS' },
  { v: 'other', label: 'Other' },
];

const PRIORITIES = ['low', 'medium', 'high', 'critical'];

export default function ReportIssueButton() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', module: 'other', priority: 'medium',
    screenshot: null,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) return toast.error('Title too short');
    if (form.description.trim().length < 10) return toast.error('Description too short (min 10 chars)');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('module', form.module);
      fd.append('priority', form.priority);
      if (form.screenshot) fd.append('screenshot', form.screenshot);
      const r = await api.post('/issues/report', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Issue submitted · ${r.data.issue_id}`);
      setOpen(false);
      setForm({ title: '', description: '', module: 'other', priority: 'medium', screenshot: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="report-issue-fab"
        className="fixed bottom-20 lg:bottom-4 left-4 z-30 flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-red-600 text-white rounded-full shadow-lg hover:bg-red-700 transition-all"
        title="Report a bug or issue"
        aria-label="Report an issue"
      >
        <MessageCircleWarning className="w-4 h-4" />
        <span className="hidden sm:inline">Report Issue</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="report-issue-modal">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <MessageCircleWarning className="w-4 h-4 text-red-600" /> Report an Issue
              </h3>
              <button onClick={() => setOpen(false)} aria-label="Close" className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submit} className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="What went wrong in one line?"
                  data-testid="issue-title-input"
                  className="w-full mt-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  maxLength={200}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Module</label>
                  <select
                    value={form.module}
                    onChange={e => setForm({ ...form, module: e.target.value })}
                    data-testid="issue-module-select"
                    className="w-full mt-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  >
                    {MODULES.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Priority</label>
                  <select
                    value={form.priority}
                    onChange={e => setForm({ ...form, priority: e.target.value })}
                    data-testid="issue-priority-select"
                    className="w-full mt-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  >
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Description *</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Steps to reproduce, what you expected, what actually happened..."
                  rows={5}
                  data-testid="issue-description-input"
                  className="w-full mt-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  maxLength={5000}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Screenshot (optional, max 5MB)</label>
                <label className="mt-1 flex items-center justify-center gap-2 w-full px-3 py-2 text-sm border-2 border-dashed border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/50">
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">
                    {form.screenshot ? form.screenshot.name : 'Click to upload an image'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="issue-screenshot-input"
                    onChange={e => setForm({ ...form, screenshot: e.target.files?.[0] || null })}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  data-testid="issue-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="issue-submit-btn"
                  className="px-4 py-1.5 text-sm font-bold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting…' : 'Submit Issue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
